import { AccountInfo } from "@azure/msal-browser";
import { UserProfile } from "@/types/user";

/**
 * Extracts user information from Microsoft's AccountInfo and formats it for our app
 */
export const extractUserProfile = (account: AccountInfo): UserProfile => {
  // Extract basic information from the account
  const {
    homeAccountId,
    localAccountId,
    username,
    name,
    tenantId,
    environment,
    ...otherAccountInfo
  } = account;

  // Parse the homeAccountId to get tenant and object ID
  const [tenantIdFromHome, objectId] = homeAccountId.split(".");

  // Extract email from username (usually username is the email)
  const email = username.includes("@") ? username : "";

  // Parse display name into given name and surname
  const nameParts = (name || "").split(" ");
  const givenName = nameParts[0] || "";
  const surname = nameParts.slice(1).join(" ") || "";

  // Generate initials from the name
  const initials = (name || "")
    .split(" ")
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return {
    // Basic information
    id: localAccountId,
    displayName: name || username,
    givenName,
    surname,
    userPrincipalName: username,
    mail: email,

    // Computed fields
    fullName: name || username,
    initials,
    email: email || username,

    // Microsoft specific
    tenantId: tenantIdFromHome || tenantId,
    homeAccountId,
    username,

    // Default values for required fields
    accountEnabled: true,

    // Additional fields that might be available (will be undefined for now)
    jobTitle: undefined,
    department: undefined,
    companyName: undefined,
    officeLocation: undefined,
    mobilePhone: undefined,
    businessPhones: undefined,
    usageLocation: undefined,
    preferredLanguage: undefined,
    timeZone: undefined,
  };
};

/**
 * Gets a greeting message based on the current time and user's name
 */
export const getGreeting = (
  userName: string,
  language: string = "ar"
): string => {
  const hour = new Date().getHours();
  let greeting = "";

  if (language === "ar") {
    if (hour < 12) {
      greeting = "صباح الخير";
    } else if (hour < 17) {
      greeting = "مساء الخير";
    } else {
      greeting = "مساء الخير";
    }
  } else {
    if (hour < 12) {
      greeting = "Good morning";
    } else if (hour < 17) {
      greeting = "Good afternoon";
    } else {
      greeting = "Good evening";
    }
  }

  return `${greeting} ${userName} 👋`;
};

/**
 * Formats the user's full name
 */
export const formatFullName = (user: UserProfile): string => {
  if (user.givenName && user.surname) {
    return `${user.givenName} ${user.surname}`;
  }
  return user.displayName || user.username;
};

/**
 * Gets the user's email address
 */
export const getUserEmail = (user: UserProfile): string => {
  return user.mail || user.userPrincipalName || user.username;
};

/**
 * Gets the user's job title and department
 */
export const getUserRole = (user: UserProfile): string => {
  const parts = [];
  if (user.jobTitle) parts.push(user.jobTitle);
  if (user.department) parts.push(user.department);
  if (user.companyName) parts.push(user.companyName);

  return parts.join(" • ") || "مستخدم";
};

/**
 * Checks if the user has a profile picture
 */
export const hasProfilePicture = (user: UserProfile): boolean => {
  // This would need to be implemented with Microsoft Graph API
  // For now, return false
  return false;
};

/**
 * Gets the user's profile picture URL (placeholder for now)
 */
export const getProfilePictureUrl = (user: UserProfile): string => {
  // This would need to be implemented with Microsoft Graph API
  // For now, return a placeholder
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(
    user.initials
  )}&background=3b82f6&color=ffffff&size=128`;
};
